// utils/uniqueSlug.ts

import { CMSCollections } from "@repo/common/src/types/firestore/collections";
import { type Firestore, collection, getDocs, limit, query, where } from "firebase/firestore";
import slugify from "slugify";

/**
 * Generates a unique slug for a document in a Firestore collection by querying the collection
 * and checking if the slug is already in use. If the slug is already in use, a counter is added
 * to the slug and the query is run again.
 */
export async function generateUniqueSlug(
  db: Firestore,
  collectionPath: CMSCollections,
  baseSlug: string | undefined,
  entityId: string | undefined,
): Promise<string> {
  if (!baseSlug) return ""; // Return empty string if baseSlug is undefined

  const slug = slugify(baseSlug, {
    lower: true,
    strict: true,
    trim: true,
  });

  let uniqueSlug = slug;
  let counter = 1;

  while (true) {
    const docs = await getDocs(query(collection(db, collectionPath), where("slug", "==", uniqueSlug), limit(2)));
    const conflictingDoc = docs.docs.find((docSnap) => docSnap.id !== entityId);
    const isUnique = !conflictingDoc;

    if (isUnique) break;

    uniqueSlug = `${slug}-${counter}`;
    counter++;
  }

  return uniqueSlug;
}
